import { useCallback, useEffect, useRef, useState } from 'react'
import { hasSubmittedAnswer, toAnswerArray } from './answerArray'

export type SolutionPreviewScope = 'session' | 'pause'

export function solutionPreviewKey(
  scope: SolutionPreviewScope,
  configComponentId: string,
  quizId: string,
): string {
  return `${scope}:${configComponentId}:${quizId}`
}

function answersEqual(left: string[] | undefined, right: string[]): boolean {
  return left?.length === right.length && left.every((value, index) => value === right[index])
}

export function useSolutionPreviewState(
  scope: SolutionPreviewScope,
  configComponentId: string,
  options?: {
    draftResetKey?: number
    savedAnswers?: Record<string, unknown>
    saving?: boolean
    onDraftStateChange?: (state: { dirty: boolean; saving: boolean }) => void
  },
) {
  const [openKeys, setOpenKeys] = useState<Set<string>>(() => new Set())
  const [draftsByKey, setDraftsByKey] = useState<Map<string, string[]>>(() => new Map())

  const stateCallback = useRef(options?.onDraftStateChange)
  stateCallback.current = options?.onDraftStateChange
  const prefix = `${scope}:${configComponentId}:`
  const dirty = Array.from(draftsByKey.entries()).some(([key, answers]) => {
    if (!key.startsWith(prefix)) return false
    const saved = options?.savedAnswers?.[key.slice(prefix.length)]
    if (!hasSubmittedAnswer(saved) && !hasSubmittedAnswer(answers)) return false
    return !answersEqual(toAnswerArray(saved), answers)
  })
  const saving = options?.saving === true
  useEffect(() => { stateCallback.current?.({ dirty, saving }) }, [dirty, saving])
  useEffect(() => () => stateCallback.current?.({ dirty: false, saving: false }), [])
  useEffect(() => {
    if (options?.draftResetKey == null) return
    setOpenKeys(new Set())
    setDraftsByKey(new Map())
  }, [options?.draftResetKey])

  const keyFor = useCallback(
    (quizId: string) => solutionPreviewKey(scope, configComponentId, quizId),
    [configComponentId, scope],
  )

  const isOpen = useCallback(
    (quizId: string) => openKeys.has(keyFor(quizId)),
    [keyFor, openKeys],
  )

  const open = useCallback((quizId: string) => {
    const key = keyFor(quizId)
    setOpenKeys((previous) => {
      if (previous.has(key)) return previous
      return new Set(previous).add(key)
    })
  }, [keyFor])

  const close = useCallback((quizId: string) => {
    const key = keyFor(quizId)
    setOpenKeys((previous) => {
      if (!previous.has(key)) return previous
      const next = new Set(previous)
      next.delete(key)
      return next
    })
  }, [keyFor])

  const rememberDraft = useCallback((quizId: string, answers: string[]) => {
    const key = keyFor(quizId)
    setDraftsByKey((previous) => {
      if (answersEqual(previous.get(key), answers)) return previous
      const next = new Map(previous)
      next.set(key, [...answers])
      return next
    })
  }, [keyFor])

  const getDraft = useCallback(
    (quizId: string) => draftsByKey.get(keyFor(quizId)),
    [draftsByKey, keyFor],
  )

  const clear = useCallback((quizId: string) => {
    const key = keyFor(quizId)
    setOpenKeys((previous) => {
      if (!previous.has(key)) return previous
      const next = new Set(previous)
      next.delete(key)
      return next
    })
    setDraftsByKey((previous) => {
      if (!previous.has(key)) return previous
      const next = new Map(previous)
      next.delete(key)
      return next
    })
  }, [keyFor])

  return { isOpen, open, close, rememberDraft, getDraft, clear }
}
